import React from 'react';
import { ApiKey } from '../../types/apiKey';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Trash2, Shield } from 'lucide-react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '../ui/alert-dialog';

interface ApiKeysListProps {
  apiKeys: ApiKey[];
  onRevoke: (id: string) => void;
  loading?: boolean;
}

export const ApiKeysList: React.FC<ApiKeysListProps> = ({
  apiKeys,
  onRevoke,
  loading = false,
}) => {
  const getStatusBadge = (apiKey: ApiKey) => {
    if (apiKey.isRevoked) {
      return <Badge variant="destructive">Revoked</Badge>;
    }
    if (apiKey.isExpired) {
      return <Badge variant="outline" className="border-orange-500 text-orange-700">Expired</Badge>;
    }
    return <Badge variant="default" className="bg-green-600">Active</Badge>;
  };

  const formatDate = (date: Date) => {
    return new Intl.DateTimeFormat('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    }).format(date);
  };

  if (apiKeys.length === 0) {
    return (
      <div className="text-center py-12 border border-dashed border-border rounded-lg">
        <Shield className="w-12 h-12 mx-auto mb-4 text-muted-foreground opacity-50" />
        <p className="text-muted-foreground">No API keys created yet</p>
        <p className="text-sm text-muted-foreground mt-1">
          Create your first API key to get started with programmatic access
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {apiKeys.map((apiKey) => (
        <div
          key={apiKey.id}
          className="border border-border rounded-lg p-4 hover:bg-muted/30 transition-colors"
        >
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <div className="flex items-center space-x-3 mb-2">
                <h4 className="font-semibold">{apiKey.name}</h4>
                {getStatusBadge(apiKey)}
              </div>
              
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-muted-foreground">Created</p>
                  <p className="font-medium">{formatDate(apiKey.createdAt)}</p>
                </div>
                <div>
                  <p className="text-muted-foreground">Expires</p>
                  <p className="font-medium">{formatDate(apiKey.expiresAt)}</p>
                </div>
              </div>

              {apiKey.isRevoked && apiKey.revokedAt && (
                <div className="mt-2 text-sm">
                  <p className="text-muted-foreground">
                    Revoked on {formatDate(apiKey.revokedAt)}
                  </p>
                </div>
              )}
            </div>

            <div className="flex items-center space-x-2 ml-4">
              {!apiKey.isRevoked && !apiKey.isExpired && (
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <Button
                      variant="ghost"
                      size="sm"
                      disabled={loading}
                      className="text-destructive hover:text-destructive hover:bg-destructive/10"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle>Revoke API Key?</AlertDialogTitle>
                      <AlertDialogDescription>
                        This will immediately revoke the API key "{apiKey.name}".
                        Any applications using this key will no longer be able to
                        authenticate. This action cannot be undone.
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel>Cancel</AlertDialogCancel>
                      <AlertDialogAction
                        onClick={() => onRevoke(apiKey.id)}
                        className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                      >
                        Revoke Key
                      </AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              )}
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};
